import axios from 'axios';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Store, User, ChevronRight, RefreshCw, MessageSquare, CheckCircle, Calendar } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "../lib/PermissionsContext";
import { useAuth } from "../lib/AuthContext";

const API = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const fmt = v => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(v || 0);

export function LMDSNonPayees() {
  const navigate = useNavigate();
  const { canDo } = usePermissions();
  const { user, isInstallateur, isProprietaire } = useAuth();
  const canModify = canDo('lmds_non_payees_modifier');
  const canActualiser = canDo('lmds_non_payees_actualiser');
  // ✅ Le propriétaire bypasse la restriction
  const voirPropresSeulement = !isProprietaire && canDo('lmds_non_payees_voir_propres_seulement');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreInstallateur, setFiltreInstallateur] = useState("tous");
  const [resolveDialog, setResolveDialog] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [targetSemaine, setTargetSemaine] = useState("");
  const [targetAnnee, setTargetAnnee] = useState(new Date().getFullYear());

  async function fetchNonPayees() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/lmds/toutes-non-payees`);
      if (r.status !== 200) throw new Error();
      let data = r.data;
      // 🆕 Si l'utilisateur ne peut voir que ses propres installations
      if (voirPropresSeulement && user?.employee_id) {
        data = data.filter(i => i.installateur_id === user.employee_id);
      }
      setItems(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchNonPayees(); }, []);

  const handleResolve = async (action) => {
    if (!resolveDialog) return;
    if (action === 'payee_semaine' && (!targetSemaine || !targetAnnee)) {
      toast.error("Veuillez sélectionner une semaine cible");
      return;
    }
    setResolving(true);
    try {
      const r = await axios.post(`${API}/api/lmds/resolve-non-payee`, {
          source_annee: resolveDialog.annee,
          source_semaine: resolveDialog.semaine,
          ligne_id: resolveDialog.id || resolveDialog.ligne_id,
          installateur_id: resolveDialog.installateur_id,
          action,
          target_annee: parseInt(targetAnnee)
        });
      const d = r.data;
      if (r.status === 200) {
        toast.success(d.message || "Installation résolue");
        setResolveDialog(null);
        fetchNonPayees();
      } else {
        toast.error(d.detail || "Erreur");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setResolving(false);
    }
  };

  // Liste des installateurs distincts
  const installateursList = [...new Map(items.map(i => [i.installateur_id, i.installateur_nom])).entries()]
    .map(([id, nom]) => ({ id, nom }))
    .sort((a, b) => a.nom.localeCompare(b.nom));

  const filtered = filtreInstallateur === "tous"
    ? items
    : items.filter(i => i.installateur_id === filtreInstallateur);

  // Grouper par installateur
  const groupes = filtered.reduce((acc, item) => {
    const key = item.installateur_id || "inconnu";
    if (!acc[key]) acc[key] = { nom: item.installateur_nom || "Inconnu", id: key, lignes: [] };
    acc[key].lignes.push(item);
    return acc;
  }, {});

  const totalMontant = filtered.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate("/sous-traitants/lmds")}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Sous-traitants LMDS
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Installations non payées</h1>
              <p className="text-slate-500 text-sm mt-0.5">Vue centralisée de toutes les installations en attente de paiement</p>
            </div>
          </div>
        </div>
        {canActualiser && (
        <button
          onClick={fetchNonPayees}
          data-testid="refresh-non-payees-btn"
          className="flex items-center gap-2 border border-slate-200 px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 overflow-hidden">
          <p className="text-[10px] text-red-600 uppercase tracking-wide font-medium truncate">Non payées</p>
          <p className="text-xl font-bold text-red-700 mt-1" data-testid="total-non-payees-count">{items.length}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 overflow-hidden">
          <p className="text-[10px] text-red-600 uppercase tracking-wide font-medium truncate">Montant total</p>
          <p className="text-lg font-bold text-red-700 mt-1 truncate" data-testid="total-non-payees-montant">{fmt(totalMontant)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3 overflow-hidden">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium truncate">Installateurs</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{installateursList.length}</p>
        </div>
      </div>

      {/* Filtre par installateur */}
      {installateursList.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500 font-medium">Filtrer :</span>
          <button
            onClick={() => setFiltreInstallateur("tous")}
            data-testid="filtre-tous-btn"
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filtreInstallateur === "tous"
                ? "bg-[#1a237e] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Tous ({items.length})
          </button>
          {installateursList.map(inst => {
            const count = items.filter(i => i.installateur_id === inst.id).length;
            return (
              <button
                key={inst.id}
                onClick={() => setFiltreInstallateur(inst.id)}
                data-testid={`filtre-inst-${inst.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filtreInstallateur === inst.id
                    ? "bg-red-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {inst.nom} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-emerald-600" />
          </div>
          <p className="font-semibold text-emerald-800 text-lg">Aucune installation non payée</p>
          <p className="text-emerald-600 text-sm mt-1">Tous les installateurs sont à jour !</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.values(groupes).map(groupe => {
            const sousTotal = groupe.lignes.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
            return (
              <div key={groupe.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`groupe-${groupe.id}`}>
                {/* En-tête du groupe */}
                <div className="flex items-center justify-between px-5 py-3 bg-red-600 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">{groupe.nom}</p>
                      <p className="text-xs text-red-200">{groupe.lignes.length} installation(s) non payée(s)</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{fmt(sousTotal)}</p>
                    <button
                      onClick={() => navigate(`/sous-traitants/lmds/${groupe.id}`)}
                      data-testid={`voir-rapports-${groupe.id}`}
                      className="flex items-center gap-1 text-xs text-red-200 hover:text-white transition-colors mt-0.5"
                    >
                      Voir les rapports <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Tableau des lignes */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`table-${groupe.id}`}>
                    <thead>
                      <tr className="bg-red-50 border-b border-red-100">
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-red-700 uppercase tracking-wide">Client</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-red-700 uppercase tracking-wide">Semaine</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-red-700 uppercase tracking-wide">Date</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-red-700 uppercase tracking-wide">SI</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-red-700 uppercase tracking-wide">Total</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-red-700 uppercase tracking-wide w-20">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupe.lignes.map((ligne, idx) => (
                        <tr
                          key={`${ligne.annee}-${ligne.semaine}-${idx}`}
                          className="border-b border-red-50 hover:bg-red-50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                              <span className="font-medium text-slate-900">{ligne.nom_client || "—"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Sem. {ligne.semaine} — {ligne.annee}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-slate-600">
                            {ligne.date_rdv || ligne.date || "—"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {ligne.si ? (
                              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                {ligne.si}
                              </span>
                            ) : "—"}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-red-700">
                            {fmt(ligne.total)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center gap-1 justify-center">
                              {canModify && (
                                <button
                                  data-testid={`resolve-btn-${groupe.id}-${idx}`}
                                  onClick={() => {
                                    setResolveDialog({ ...ligne, installateur_id: groupe.id });
                                    setTargetSemaine("");
                                    setTargetAnnee(new Date().getFullYear());
                                  }}
                                  title="Résoudre"
                                  className="flex items-center gap-1 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-2 py-1 rounded transition-colors"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                  Résoudre
                                </button>
                              )}
                              {!isInstallateur && (
                                <div 
                                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                                    ligne.last_reminder_date 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-slate-100 text-slate-500'
                                  }`}
                                  title={ligne.last_reminder_date 
                                    ? `Rappel envoyé le ${new Date(ligne.last_reminder_date).toLocaleDateString('fr-CA')}`
                                    : 'Aucun rappel envoyé'}
                                  data-testid={`sms-indicator-${groupe.id}-${idx}`}
                                >
                                  <MessageSquare className="w-3 h-3" />
                                  {ligne.last_reminder_date ? 'SMS ✓' : 'SMS'}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {groupe.lignes.length > 1 && (
                      <tfoot>
                        <tr className="bg-red-50 border-t border-red-200">
                          <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-red-700 uppercase">
                            Sous-total {groupe.nom}
                          </td>
                          <td className="px-4 py-2 text-right font-bold text-red-700">{fmt(sousTotal)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog Résolution */}
      {resolveDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResolveDialog(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Résoudre — {resolveDialog.nom_client}</h2>
              <p className="text-sm text-slate-500 mt-1">
                Sem. {resolveDialog.semaine}/{resolveDialog.annee} · {fmt(resolveDialog.total || resolveDialog.montant_contrat)}
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Payée dans la semaine :</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Semaine</label>
                  <input
                    type="number"
                    min="1"
                    max="53"
                    value={targetSemaine}
                    onChange={e => setTargetSemaine(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Ex: 12"
                    data-testid="resolve-target-semaine"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Année</label>
                  <select
                    value={targetAnnee}
                    onChange={e => setTargetAnnee(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="resolve-target-annee"
                  >
                    <option value="2025">2025</option>
                    <option value="2026">2026</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => handleResolve('payee_semaine')}
                disabled={resolving || !targetSemaine}
                data-testid="resolve-confirm-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                {resolving ? 'En cours...' : 'Payée cette semaine'}
              </button>
              <button
                onClick={() => handleResolve('annuler')}
                disabled={resolving}
                data-testid="resolve-cancel-debt-btn"
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
              >
                Annuler la dette
              </button>
            </div>
            <button
              onClick={() => setResolveDialog(null)}
              className="w-full text-center text-sm text-slate-400 hover:text-slate-600 py-1"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
